import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Globe, TrendingUp, BarChart3, Users, MapPin, Phone, Mail, Edit, Save, X, Calendar, Activity } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';

const COUNTRY_FLAGS = {
  'GN': '🇬🇳',
  'TD': '🇹🇩',
  'ML': '🇲🇱',
  'CF': '🇨🇫',
  'SN': '🇸🇳'
};

export default function Franchise({ user, onLogout }) {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFranchise, setSelectedFranchise] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(null);

  useEffect(() => {
    loadFranchises();
  }, []);

  const loadFranchises = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/franchises`);
      setFranchises(response.data);
    } catch (error) {
      console.error('Error loading franchises:', error);
      toast.error('Erreur lors du chargement des franchises');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (franchise) => {
    setSelectedFranchise(franchise);
    setEditedData(franchise);
    setShowDetailsModal(true);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData(selectedFranchise);
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/franchises/${selectedFranchise.id}`,
        editedData
      );
      toast.success('Franchise mise à jour avec succès');
      setIsEditing(false);
      setShowDetailsModal(false);
      loadFranchises();
    } catch (error) {
      console.error('Error updating franchise:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleChange = (field, value) => {
    setEditedData({ ...editedData, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    setEditedData({
      ...editedData,
      [parent]: {
        ...editedData[parent],
        [field]: value
      }
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getTotalRevenue = () => {
    return franchises.reduce((sum, f) => {
      // Handle both old (statistiques.revenus) and new (chiffre_affaires.annuel) structure
      const revenue = f.chiffre_affaires?.annuel || f.statistiques?.revenus || 0;
      return sum + revenue;
    }, 0);
  };

  const getTotalPanneaux = () => {
    return franchises.reduce((sum, f) => {
      // Handle both old (statistiques.panneaux) and new (panneaux.total) structure
      const panneaux = f.panneaux?.total || f.statistiques?.panneaux || 0;
      return sum + panneaux;
    }, 0);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="franchise">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="franchise">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
              <Globe className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Franchises SESAM</h1>
              <p className="text-gray-600">Réseau international de panneaux publicitaires</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Franchises Actives</p>
                  <p className="text-3xl font-bold text-blue-600">{franchises.length}</p>
                  <p className="text-xs text-gray-500 mt-1">Pays partenaires</p>
                </div>
                <MapPin className="h-12 w-12 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Panneaux Totaux</p>
                  <p className="text-3xl font-bold text-purple-600">{getTotalPanneaux()}</p>
                  <p className="text-xs text-gray-500 mt-1">LED + Déroulants</p>
                </div>
                <BarChart3 className="h-12 w-12 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">CA Annuel Total</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(getTotalRevenue())}</p>
                  <p className="text-xs text-gray-500 mt-1">Toutes franchises</p>
                </div>
                <TrendingUp className="h-12 w-12 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Franchises Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {franchises.map((franchise) => (
            <Card 
              key={franchise.id} 
              className="cursor-pointer hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-400"
              onClick={() => handleCardClick(franchise)}
            >
              <CardHeader className="bg-gradient-to-r from-blue-500 to-teal-500 text-white">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{COUNTRY_FLAGS[franchise.code_pays] || '🌍'}</span>
                    <div>
                      <h3 className="text-xl font-bold">{franchise.pays}</h3>
                      <p className="text-sm text-blue-100">{franchise.ville_principale}</p>
                    </div>
                  </div>
                  <Badge className={`${franchise.statut === 'Actif' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                    {franchise.statut}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                {/* Panneaux Stats */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <p className="text-xs text-gray-600">Total Panneaux</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {franchise.panneaux?.total || franchise.statistiques?.panneaux || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                    <p className="text-xs text-gray-600">Actifs</p>
                    <p className="text-2xl font-bold text-green-600">
                      {franchise.panneaux?.actifs || Math.floor((franchise.statistiques?.panneaux || 0) * 0.8)}
                    </p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                    <p className="text-xs text-gray-600">LED</p>
                    <p className="text-xl font-bold text-purple-600">
                      {franchise.panneaux?.led || Math.floor((franchise.statistiques?.panneaux || 0) * 0.4)}
                    </p>
                  </div>
                  <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                    <p className="text-xs text-gray-600">Déroulants</p>
                    <p className="text-xl font-bold text-orange-600">
                      {franchise.panneaux?.deroulants || Math.floor((franchise.statistiques?.panneaux || 0) * 0.6)}
                    </p>
                  </div>
                </div>

                {/* Revenue */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">CA Mensuel</span>
                    <span className="font-bold text-blue-600">
                      {formatCurrency(franchise.chiffre_affaires?.mensuel || (franchise.statistiques?.revenus / 12) || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">CA Annuel</span>
                    <span className="font-bold text-green-600">
                      {formatCurrency(franchise.chiffre_affaires?.annuel || franchise.statistiques?.revenus || 0)}
                    </span>
                  </div>
                </div>

                {/* Contact */}
                <div className="border-t pt-4">
                  <p className="text-xs text-gray-600 mb-2">Responsable</p>
                  <p className="font-semibold text-gray-800">
                    {franchise.contact?.responsable || franchise.contact || 'Non défini'}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Details Modal */}
      {selectedFranchise && (
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{COUNTRY_FLAGS[selectedFranchise.code_pays] || '🌍'}</span>
                  <div>
                    <h3 className="text-2xl font-bold">{selectedFranchise.pays}</h3>
                    <p className="text-sm text-gray-600">{selectedFranchise.ville_principale}</p>
                  </div>
                </div>
                {!isEditing ? (
                  <Button onClick={handleEdit} className="bg-blue-600 hover:bg-blue-700">
                    <Edit className="h-4 w-4 mr-2" />
                    Modifier
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleCancel}>
                      <X className="h-4 w-4 mr-2" />
                      Annuler
                    </Button>
                    <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                      <Save className="h-4 w-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                )}
              </DialogTitle>
              <DialogDescription>
                Détails complets de la franchise
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 mt-4">
              {/* Status & Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Activity className="h-4 w-4" />
                    Statut
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editedData.statut}
                      onChange={(e) => handleChange('statut', e.target.value)}
                    />
                  ) : (
                    <Badge className={`${selectedFranchise.statut === 'Actif' ? 'bg-green-500' : 'bg-yellow-500'}`}>
                      {selectedFranchise.statut}
                    </Badge>
                  )}
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="h-4 w-4" />
                    Date d'ouverture
                  </Label>
                  <p className="text-gray-700">{new Date(selectedFranchise.date_ouverture).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>

              {/* Panneaux Statistics */}
              <div>
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Statistiques des Panneaux
                </h4>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <Label>Total</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedData.panneaux.total}
                        onChange={(e) => handleNestedChange('panneaux', 'total', parseInt(e.target.value))}
                      />
                    ) : (
                      <p className="text-2xl font-bold text-blue-600">{selectedFranchise.panneaux.total}</p>
                    )}
                  </div>
                  <div>
                    <Label>Actifs</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedData.panneaux.actifs}
                        onChange={(e) => handleNestedChange('panneaux', 'actifs', parseInt(e.target.value))}
                      />
                    ) : (
                      <p className="text-2xl font-bold text-green-600">{selectedFranchise.panneaux.actifs}</p>
                    )}
                  </div>
                  <div>
                    <Label>LED</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedData.panneaux.led}
                        onChange={(e) => handleNestedChange('panneaux', 'led', parseInt(e.target.value))}
                      />
                    ) : (
                      <p className="text-2xl font-bold text-purple-600">{selectedFranchise.panneaux.led}</p>
                    )}
                  </div>
                  <div>
                    <Label>Déroulants</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedData.panneaux.deroulants}
                        onChange={(e) => handleNestedChange('panneaux', 'deroulants', parseInt(e.target.value))}
                      />
                    ) : (
                      <p className="text-2xl font-bold text-orange-600">{selectedFranchise.panneaux.deroulants}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Revenue */}
              <div>
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Chiffre d'Affaires
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>CA Mensuel (FCFA)</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedData.chiffre_affaires.mensuel}
                        onChange={(e) => handleNestedChange('chiffre_affaires', 'mensuel', parseFloat(e.target.value))}
                      />
                    ) : (
                      <p className="text-xl font-bold text-blue-600">{formatCurrency(selectedFranchise.chiffre_affaires.mensuel)}</p>
                    )}
                  </div>
                  <div>
                    <Label>CA Annuel (FCFA)</Label>
                    {isEditing ? (
                      <Input
                        type="number"
                        value={editedData.chiffre_affaires.annuel}
                        onChange={(e) => handleNestedChange('chiffre_affaires', 'annuel', parseFloat(e.target.value))}
                      />
                    ) : (
                      <p className="text-xl font-bold text-green-600">{formatCurrency(selectedFranchise.chiffre_affaires.annuel)}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-purple-600" />
                  Contact
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Responsable
                    </Label>
                    {isEditing ? (
                      <Input
                        value={editedData.contact.responsable}
                        onChange={(e) => handleNestedChange('contact', 'responsable', e.target.value)}
                      />
                    ) : (
                      <p className="text-gray-700">{selectedFranchise.contact.responsable}</p>
                    )}
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Téléphone
                    </Label>
                    {isEditing ? (
                      <Input
                        value={editedData.contact.telephone}
                        onChange={(e) => handleNestedChange('contact', 'telephone', e.target.value)}
                      />
                    ) : (
                      <p className="text-gray-700">{selectedFranchise.contact.telephone}</p>
                    )}
                  </div>
                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </Label>
                    {isEditing ? (
                      <Input
                        type="email"
                        value={editedData.contact.email}
                        onChange={(e) => handleNestedChange('contact', 'email', e.target.value)}
                      />
                    ) : (
                      <p className="text-gray-700">{selectedFranchise.contact.email}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
}
